/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { GetStaticProps, NextLayoutComponentType } from 'next';

import { Api } from '@/api';
import MainLayout from '@/layouts/main';
import AppTitle from '@/components/common/title';
import RecitersList from '@/components/reciters/list';
import { routes } from '@/infrastructure/const/routes';
import AppContainer from '@/components/layout/container';
import { localeList } from '@/public/locales/locale-list';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import s from './styles.module.scss';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const api = Api.getInstance();

interface IMostPopularPageProps {
  reciters: IReciter[];
}

const MostPopularPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IMostPopularPageProps>(data);
  const { reciters } = decrypted;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tMeta } = useTranslation('meta');
  const { t: tCommon } = useTranslation('common');
  const { t: tLinks } = useTranslation('links');

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('mostPopular'),
      path: routes.mostPopular,
    },
  ];

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <Head>
        <title>{tMeta('mostPopularPageTitle')}</title>
        <meta
          name="description"
          content={tMeta('mostPopularPageDescription')}
        />
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <main>
        <AppContainer>
          <AppTitle title={tCommon('mostPopular')} main />
          <RecitersList
            className={s['list']}
            items={sortArrayByFieldName(reciters, 'order')}
          />
        </AppContainer>
      </main>
    </>
  );
};

MostPopularPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  try {
    const { data: recitersResult } = await api.getPopularReciters(
      allRecordsPaginationOptions,
      ['image', 'slug', 'title_en', 'title_ar', 'order']
    );
    const { results: reciters } = recitersResult;
    reciters.map((reciter) => {
      addDomainForReciterImages(reciter);
    });

    const dataToEncrypt = {
      reciters,
    };
    const { encryptPageProps } = await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default MostPopularPage;
